/**
 * Where the id port's vocabulary meets the composer's.
 *
 * The port itself is published through the port registry (`ports.tsx`), beside
 * the clock and the exporter, because minting an id is a capability in exactly
 * that sense and `browserPorts.ts` is "the one place browser adapters are
 * constructed". A private context for this one port would have been a second
 * registry — a second answer to which source an id came from, and a fake bundle
 * whose ids came from the real `crypto` while every other port was a double.
 *
 * What is genuinely local to this file is the *renaming*. The composer names its
 * minters for the thing it is about to create (`nextBlockId`) while the port
 * names its members for the kind of identity they mint (`journalBlockId`), and
 * this is the one place those two vocabularies meet.
 */

import { useMemo } from "react";
import type { WorkoutIdMinter } from "@ff/domain";
import type { IdPort } from "@ff/domain/ports";
import type { ComboIdSource } from "@ff/combo";

import type { JournalComposerIdentity } from "../features/journal/composer/seeds";
import { useIds } from "./ports";

/**
 * The composer's three minters, over one port.
 *
 * All three are direct. Written as a function over an `IdPort` rather than only
 * as a hook so a test can state the claim — three *independent* ids, not one
 * branded three ways — against a counting source without rendering anything.
 */
export function journalComposerIdentity(ids: IdPort): JournalComposerIdentity {
  return {
    nextEntryId: () => ids.journalEntryId(),
    nextGoalId: () => ids.journalGoalId(),
    nextBlockId: () => ids.journalBlockId(),
  };
}

export function useJournalComposerIdentity(): JournalComposerIdentity {
  const ids = useIds();
  return useMemo(() => journalComposerIdentity(ids), [ids]);
}

/**
 * The builder's three minters, over one port.
 *
 * `WorkoutIdMinter` is `@ff/domain`'s own shape — `materializeWorkoutPlan` and
 * `newGuidedWorkoutPlan` both take it — so this is only the renaming again:
 * `mint.section()` on one side, `ids.workoutSectionId()` on the other. Both the
 * Build workspace and the Workout Tape mint plans, so binding it here is what
 * stops the two surfaces from growing two different id sources for one library.
 */
export function workoutIdentity(ids: IdPort): WorkoutIdMinter {
  return {
    plan: () => ids.workoutPlanId(),
    section: () => ids.workoutSectionId(),
    interval: () => ids.workoutIntervalId(),
  };
}

export function useWorkoutIdentity(): WorkoutIdMinter {
  const ids = useIds();
  return useMemo(() => workoutIdentity(ids), [ids]);
}

/**
 * The combo editor's four minters, over one port.
 *
 * `ComboIdSource` is `@ff/combo`'s shape, and its own doc explains why editing
 * cannot derive these: a duplicated beat must differ from the beat it was copied
 * from. Each member maps to its own kind rather than to one shared string,
 * because a beat id laundered into a branch id is exactly the confusion the
 * branding exists to catch.
 */
export function comboIdentity(ids: IdPort): ComboIdSource {
  return {
    beat: () => ids.comboBeatId(),
    branch: () => ids.responseBranchId(),
    option: () => ids.responseOptionId(),
    phraseBracket: () => ids.phraseBracketId(),
  };
}

export function useComboIdentity(): ComboIdSource {
  const ids = useIds();
  return useMemo(() => comboIdentity(ids), [ids]);
}
